/**
 * Rate-Limited Firestore Service
 * 
 * Wraps firestoreService with rate limiting to prevent bot abuse and excessive costs.
 * 
 * CRITICAL: This protects against bots that could spam Firestore operations,
 * potentially costing hundreds or thousands of dollars per month.
 * 
 * Usage: Replace all instances of firestoreService with rateLimitedFirestoreService
 */

import { httpsCallable, Functions } from 'firebase/functions';
import { functions } from '../firebase';
import { firestoreService } from './firestoreService';
import { UserProfile, Quest, DailyActivity, Program } from '../types';

const rateLimiter = httpsCallable(functions, 'rateLimiterCallable');

// Rate limit configuration - tuned for legitimate user behavior
const RATE_LIMITS = {
  quest_create: { maxAttempts: 30, windowMs: 60000, blockDurationMs: 900000 },      // 30/min, 15min block
  quest_update: { maxAttempts: 60, windowMs: 60000, blockDurationMs: 900000 },      // 60/min, 15min block
  quest_time_block: { maxAttempts: 200, windowMs: 60000, blockDurationMs: 900000 }, // 200/min, 15min block - lenient for calendar adjustments
  quest_delete: { maxAttempts: 20, windowMs: 60000, blockDurationMs: 900000 },      // 20/min, 15min block
  quest_bulk_delete: { maxAttempts: 10, windowMs: 60000, blockDurationMs: 900000 }, // 10 bulk ops/min, 15min block
  program_create: { maxAttempts: 5, windowMs: 300000, blockDurationMs: 900000 },    // 5 per 5 min, 15min block
  program_delete: { maxAttempts: 10, windowMs: 60000, blockDurationMs: 900000 },    // 10/min, 15min block
  profile_update: { maxAttempts: 30, windowMs: 60000, blockDurationMs: 900000 },    // 30/min, 15min block
  statistics_write: { maxAttempts: 20, windowMs: 60000, blockDurationMs: 900000 },  // 20/min, 15min block
  passive_skill_create: { maxAttempts: 10, windowMs: 60000, blockDurationMs: 900000 }, // 10/min, 15min block
  passive_skill_update: { maxAttempts: 30, windowMs: 60000, blockDurationMs: 900000 }, // 30/min, 15min block
  achievement_save: { maxAttempts: 20, windowMs: 60000, blockDurationMs: 900000 },  // 20/min, 15min block
};

interface RateLimitConfig {
  maxAttempts: number;
  windowMs: number;
  blockDurationMs?: number;
}

/**
 * Check rate limit before performing an action
 * @param action - The action to check
 * @param bypass - If true, skip rate limiting (for trusted internal operations)
 * @throws Error if rate limit is exceeded
 */
async function checkRateLimit(action: string, bypass: boolean = false): Promise<void> {
  // 🔓 BYPASS: Skip rate limiting for trusted operations (e.g., AI program generation)
  if (bypass) {
    console.log(`🔓 Bypassing rate limit for trusted operation: ${action}`);
    return;
  }
  
  const config = RATE_LIMITS[action as keyof typeof RATE_LIMITS];
  
  if (!config) {
    console.warn(`⚠️ No rate limit config for action: ${action}`);
    return;
  }

  try {
    const result = await rateLimiter({ 
      action,
      config
    });

    const response = result.data as any;
    
    // Check if rate limit was exceeded
    if (!response.success || !response.data.allowed) {
      const blockedUntil = response.data?.blockedUntil;
      
      if (blockedUntil) {
        const minutesRemaining = Math.ceil((blockedUntil - Date.now()) / 60000);
        throw new Error(
          `⚠️ Rate limit exceeded for ${action}.\n\nPlease wait ${minutesRemaining} minute${minutesRemaining !== 1 ? 's' : ''} before trying again.`
        );
      } else {
        throw new Error(
          `⚠️ Rate limit exceeded for ${action}.\n\nPlease wait a moment before trying again.`
        );
      }
    }
    
    // Log warning if approaching limit
    if (response.data.attemptsRemaining !== undefined && response.data.attemptsRemaining <= 3) {
      console.warn(
        `⚠️ Approaching rate limit for ${action}: ${response.data.attemptsRemaining} attempts remaining`
      );
    }
  } catch (error: any) {
    // If it's a rate limit error, re-throw it
    if (error.message?.includes('Rate limit exceeded') || error.message?.includes('⚠️')) {
      throw error;
    }
    
    // For other errors (network, function unavailable, etc.)
    // Log the error but allow the operation to proceed (fail-open)
    // TODO: Consider changing to fail-closed in production for maximum security
    console.error('⚠️ Rate limit check failed, allowing operation:', error.message);
  }
}

/**
 * Rate-Limited Firestore Service
 * 
 * All write operations are protected by rate limiting.
 * Read operations are pass-through (can add rate limiting if needed).
 */
export const rateLimitedFirestoreService = {
  // ============= WRITE OPERATIONS (RATE LIMITED) =============
  
  /**
   * Save a single quest (rate limited: 30/min)
   * @param userId - User ID
   * @param quest - Quest to save
   * @param bypassRateLimit - If true, skip rate limiting (for trusted operations like AI program generation)
   */
  async saveQuest(userId: string, quest: Quest, bypassRateLimit: boolean = false): Promise<void> {
    await checkRateLimit('quest_create', bypassRateLimit);
    return firestoreService.saveQuest(userId, quest);
  },

  /**
   * Save multiple quests (rate limited: 30/min)
   * @param userId - User ID
   * @param quests - Quests to save
   * @param bypassRateLimit - If true, skip rate limiting (for trusted operations like AI program generation)
   */
  async saveQuests(userId: string, quests: Quest[], bypassRateLimit: boolean = false): Promise<void> {
    await checkRateLimit('quest_create', bypassRateLimit);
    return firestoreService.saveQuests(userId, quests);
  },

  /**
   * Delete a quest (rate limited: 20/min)
   */
  async deleteQuest(questId: string): Promise<void> {
    await checkRateLimit('quest_delete');
    return firestoreService.deleteQuest(questId);
  },

  /**
   * Delete multiple quests in a batch (rate limited: 10 batch operations per minute)
   * Used for program deletions - more efficient than deleting one by one
   * This counts as ONE operation regardless of how many quests are deleted
   */
  async deleteBulkQuests(questIds: string[]): Promise<void> {
    await checkRateLimit('quest_bulk_delete');
    return firestoreService.deleteBulkQuests(questIds);
  },

  /**
   * Save a program (rate limited: 5 per 5 minutes)
   * Programs are expensive operations (multiple quests, milestones, etc.)
   */
  async saveProgram(userId: string, program: Program): Promise<void> {
    await checkRateLimit('program_create');
    return firestoreService.saveProgram(userId, program);
  },

  /**
   * Update an existing program (not rate limited)
   * Used for updating program progress, completion counts, etc.
   */
  async updateProgram(programId: string, updates: Partial<Program>): Promise<void> {
    // No rate limit for program updates - only for creating new programs
    return firestoreService.updateProgram(programId, updates);
  },

  /**
   * Save multiple programs (rate limited: 5 per 5 minutes)
   */
  async savePrograms(userId: string, programs: Program[]): Promise<void> {
    await checkRateLimit('program_create');
    return firestoreService.savePrograms(userId, programs);
  },

  /**
   * Update multiple existing programs (NOT rate limited)
   * Used for automatic program updates (progress, milestones, etc.) in background
   */
  async updatePrograms(userId: string, programs: Program[]): Promise<void> {
    // No rate limit for bulk program updates - these are automatic system updates
    return firestoreService.savePrograms(userId, programs);
  },

  /**
   * Delete a program (rate limited: 10/min)
   */
  async deleteProgram(programId: string): Promise<void> {
    await checkRateLimit('program_delete');
    return firestoreService.deleteProgram(programId);
  },

  /**
   * Save user profile (rate limited: 30/min)
   * Note: This is a full save, not an update
   */
  async saveUserProfile(userId: string, profile: UserProfile): Promise<void> {
    await checkRateLimit('profile_update');
    return firestoreService.saveUserProfile(userId, profile);
  },

  /**
   * Update user profile (rate limited: 30/min)
   */
  async updateUserProfile(userId: string, updates: Partial<UserProfile>): Promise<void> {
    await checkRateLimit('profile_update');
    return firestoreService.updateUserProfile(userId, updates);
  },

  /**
   * Save daily statistic (rate limited: 20/min)
   */
  async saveDailyStatistic(userId: string, statistic: DailyActivity): Promise<void> {
    await checkRateLimit('statistics_write');
    return firestoreService.saveDailyStatistic(userId, statistic);
  },

  /**
   * Save multiple statistics (rate limited: 20/min)
   */
  async saveStatistics(userId: string, statistics: DailyActivity[]): Promise<void> {
    await checkRateLimit('statistics_write');
    return firestoreService.saveStatistics(userId, statistics);
  },

  /**
   * Save passive skill (rate limited: 10/min)
   */
  async savePassiveSkill(userId: string, skill: any): Promise<string> {
    await checkRateLimit('passive_skill_create');
    return firestoreService.savePassiveSkill(userId, skill);
  },

  /**
   * Update passive skill (rate limited: 30/min)
   */
  async updatePassiveSkill(skillId: string, updates: any): Promise<void> {
    await checkRateLimit('passive_skill_update');
    return firestoreService.updatePassiveSkill(skillId, updates);
  },

  /**
   * Delete passive skill (rate limited: 30/min - uses update limit)
   */
  async deletePassiveSkill(skillId: string): Promise<void> {
    await checkRateLimit('passive_skill_update');
    return firestoreService.deletePassiveSkill(skillId);
  },

  /**
   * Save achievements (rate limited: 20/min)
   */
  async saveAchievements(userId: string, achievements: any[]): Promise<void> {
    await checkRateLimit('achievement_save');
    return firestoreService.saveAchievements(userId, achievements);
  },

  // ============= READ OPERATIONS (PASS-THROUGH) =============
  // Read operations are generally less expensive and less prone to abuse
  // Can add rate limiting if needed in the future
  
  /**
   * Get user profile (no rate limit)
   */
  getUserProfile: firestoreService.getUserProfile,

  /**
   * Get user quests (no rate limit)
   */
  getUserQuests: firestoreService.getUserQuests,

  /**
   * Get user programs (no rate limit)
   */
  getUserPrograms: firestoreService.getUserPrograms,

  /**
   * Get user statistics (no rate limit)
   */
  getUserStatistics: firestoreService.getUserStatistics,

  /**
   * Get user passive skills (no rate limit)
   */
  getUserPassiveSkills: firestoreService.getUserPassiveSkills,

  /**
   * Get achievements (no rate limit)
   */
  getAchievements: firestoreService.getAchievements,

  /**
   * Sync local data to Firestore (no rate limit - one-time migration)
   */
  syncLocalDataToFirestore: firestoreService.syncLocalDataToFirestore,

  // ============= FAIL-SAFE OPERATIONS (NO RATE LIMIT) =============
  // These are system operations for recovery and distributed locking
  
  /**
   * Acquire distributed lock for phase generation (no rate limit)
   * System operation for preventing duplicate generations
   */
  acquireGenerationLock: firestoreService.acquireGenerationLock,

  /**
   * Release distributed lock for phase generation (no rate limit)
   * System operation for cleanup
   */
  releaseGenerationLock: firestoreService.releaseGenerationLock,
};

/**
 * Export rate limit configuration for reference/testing
 */
export { RATE_LIMITS };

/**
 * Helper function to manually check rate limit without incrementing counter
 * Useful for showing warnings to users before they hit the limit
 */
export async function checkRateLimitStatus(action: string): Promise<{
  allowed: boolean;
  attemptsRemaining: number;
  attemptsUsed: number;
  maxAttempts: number;
  resetAt?: number;
}> {
  const config = RATE_LIMITS[action as keyof typeof RATE_LIMITS];
  
  if (!config) {
    throw new Error(`Unknown action: ${action}`);
  }

  try {
    const result = await rateLimiter({ 
      action,
      checkOnly: true // Don't increment counter
    });

    const response = result.data as any;
    
    if (response.success && response.data) {
      return {
        allowed: response.data.allowed || false,
        attemptsRemaining: response.data.attemptsRemaining || 0,
        attemptsUsed: response.data.attemptsUsed || 0,
        maxAttempts: response.data.maxAttempts || config.maxAttempts,
        resetAt: response.data.resetAt
      };
    }
    
    // Default response if check fails
    return {
      allowed: true,
      attemptsRemaining: config.maxAttempts,
      attemptsUsed: 0,
      maxAttempts: config.maxAttempts
    };
  } catch (error) {
    console.error('Error checking rate limit status:', error);
    // Return permissive defaults on error
    return {
      allowed: true,
      attemptsRemaining: config.maxAttempts,
      attemptsUsed: 0,
      maxAttempts: config.maxAttempts
    };
  }
}

